import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertCircle, ShieldCheck, Package } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProductForm from "@/components/products/ProductForm";
import {
  useCreateStoreProductMutation,
  useMyStore,
  useStoreId,
  useStoreProduct,
  useUpdateStoreProductMutation,
} from "@/hooks/queries";
import { productResponseToFormData } from "@/lib/mappers/store-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import type { ProductFormData } from "@/types/product";
import { useToast } from "@/hooks/use-toast";
import { ProductStatus } from "@/types/enums";

const StoreProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data: store } = useMyStore(Boolean(storeId));
  const isEdit = Boolean(id);
  const productId = id ? Number(id) : 0;

  const { data: product, isLoading: productLoading } = useStoreProduct(productId, isEdit);
  const createMutation = useCreateStoreProductMutation(storeId ?? 0);
  const updateMutation = useUpdateStoreProductMutation(storeId ?? 0);

  const isAdminProduct = Boolean(product?.sourceProductId || product?.isAdminProduct);
  const availableBv = store?.availableBv ?? 0;
  const currentProductBv = product?.businessVolume ?? 0;
  const currentProductStock = product?.stockQuantity ?? 0;
  const isLoading = storeLoading || (isEdit && productLoading);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!storeId) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">No store linked to your account.</p>
        <Button variant="outline" onClick={() => navigate("/store/products")}>Back to Products</Button>
      </div>
    );
  }

  if (isEdit && !product) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Product not found.</p>
        <Button variant="outline" onClick={() => navigate("/store/products")}>Back to Products</Button>
      </div>
    );
  }

  if (!isEdit && availableBv <= 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/store/products")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-heading font-bold tracking-tight">Add Product</h1>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Store BV balance is zero</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>You cannot add products until admin credits BV to your store.</p>
            <Button asChild size="sm" variant="outline">
              <Link to="/store/bv">View Receive BV</Link>
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isEdit && isAdminProduct) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/store/products")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-heading font-bold tracking-tight">{product?.name}</h1>
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-xs font-semibold">
              Admin Supplied
            </Badge>
          </div>
        </div>

        <Alert className="border-primary/40 bg-primary/5">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <AlertTitle className="text-base font-bold">Purchased Product is Locked</AlertTitle>
          <AlertDescription className="mt-2 space-y-3 text-sm text-muted-foreground">
            <p>
              This product was purchased through the wholesale supply chain. Its details (name, MRP, price, BV) and current stock quantity (<strong className="text-foreground">{product?.stockQuantity ?? 0} units</strong>) are locked and cannot be edited manually.
            </p>
            <p>
              To add more stock to this product, please purchase additional units from Wholesaler through <strong>Store Supply</strong>. Once your order is approved, the new stock will automatically be added.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild className="gap-2">
                <Link to="/store/supply">
                  <Package className="h-4 w-4" /> Go to Store Supply
                </Link>
              </Button>
              <Button variant="outline" onClick={() => navigate("/store/products")}>
                Back to Products
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Product Information (Read-Only)</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs text-muted-foreground">Product Name</p>
              <p className="font-semibold text-sm">{product?.name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Category</p>
              <p className="font-semibold text-sm">{product?.categoryName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">MRP</p>
              <p className="font-semibold text-sm">₹{product?.mrp}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Customer Selling Price</p>
              <p className="font-semibold text-sm">₹{product?.sellingPrice}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Current Stock</p>
              <p className="font-semibold text-sm text-primary">{product?.stockQuantity ?? 0} {product?.unit ?? "units"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Business Volume (BV)</p>
              <p className="font-semibold text-sm">{product?.businessVolume ?? 0} BV</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (data: ProductFormData) => {
    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id: Number(id), data });
        toast({
          title: isAdminProduct ? "Stock updated" : "Product updated",
          description: isAdminProduct
            ? "Stock quantity updated successfully."
            : "Changes submitted for admin review.",
        });
      } else {
        await createMutation.mutateAsync(data);
        toast({ title: "Product created", description: "Submitted for admin approval." });
      }
      navigate("/store/products");
    } catch (error) {
      toast({
        title: isEdit ? "Failed to update product" : "Failed to create product",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/store/products")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold tracking-tight lg:text-3xl">
            {isEdit ? (isAdminProduct ? "Manage Stock (Admin Product)" : "Edit Product") : "Add Product"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isEdit
              ? isAdminProduct
                ? "This product was supplied from Admin via wholesale. Product details, pricing, and BV are locked. You can manage your inventory stock quantity."
                : "Update any product detail freely. Changing BV or Stock Quantity calculates the difference against your available BV."
              : "Add a new product. Total Required BV (BV × Stock Quantity) is deducted from your available BV."}
          </p>
        </div>
      </div>

      {isEdit && product?.status === ProductStatus.NeedsRevision && product.rejectionReason && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Revision requested</AlertTitle>
          <AlertDescription>{product.rejectionReason}</AlertDescription>
        </Alert>
      )}

      {!isAdminProduct && (
        <div className="rounded-xl border border-border/70 bg-gradient-to-r from-amber-500/10 via-background to-teal-500/10 p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(28_90%_48%)] text-white shadow-sm font-bold text-sm">
                BV
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Store BV Account</p>
                <p className="text-lg font-bold text-foreground">
                  {availableBv.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{" "}
                  <span className="text-xs font-medium text-muted-foreground">Available BV</span>
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground max-w-md">
              {isEdit
                ? "You can edit any product detail freely. Adjusting BV or Stock Quantity calculates the net difference: additional required BV is deducted, while reductions are refunded to your balance."
                : "Creating a product allocates (Product BV × Stock Quantity) from your available store BV."}
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Product Details</CardTitle>
        </CardHeader>
        <CardContent>
          <ProductForm
            defaultValues={product ? productResponseToFormData(product) : undefined}
            onSubmit={handleSubmit}
            onCancel={() => navigate("/store/products")}
            submitLabel={isEdit ? (isAdminProduct ? "Update Stock" : "Update Product") : "Create Product"}
            pricePanel="store"
            enforceStoreBv={!isAdminProduct}
            isAdminProduct={isAdminProduct}
            isEdit={isEdit}
            availableBv={availableBv}
            currentProductBv={currentProductBv}
            currentProductStock={currentProductStock}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreProductForm;
